import { create } from 'zustand';

import type { RenderedAudio } from './sound-render.js';

/**
 * WebAudio playback of the Sound tab's rendered buffer: play/stop, a loop
 * region, scrubbing, the playhead position and a live master peak meter.
 *
 * Solo/mute are applied by re-rendering (the store renders the document
 * with the solo state applied), so playback always plays exactly one
 * finished mix. Note previews (piano roll) are a simple tone, not the
 * instrument: the instrument is heard in the next render.
 */

export interface LoopRegion {
  start: number;
  end: number;
}

export interface PlaybackState {
  playing: boolean;
  /** Playhead in seconds. */
  position: number;
  duration: number;
  loop: LoopRegion | null;
  loopOn: boolean;
  /** Monitor volume in dB (not part of the document). */
  monitorDb: number;
  /** Live output peak in dBFS (decays while stopped). */
  meterDb: number;
  meterRightDb: number;
}

export const usePlayback = create<PlaybackState>(() => ({
  playing: false,
  position: 0,
  duration: 0,
  loop: null,
  loopOn: false,
  monitorDb: 0,
  meterDb: -Infinity,
  meterRightDb: -Infinity,
}));

const MIN_DB = -72;

function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

function gainToDb(g: number): number {
  return g > 0 ? Math.max(MIN_DB, 20 * Math.log10(g)) : -Infinity;
}

class SoundPlayer {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private splitter: ChannelSplitterNode | null = null;
  private meters: [AnalyserNode, AnalyserNode] | null = null;
  private buffer: AudioBuffer | null = null;
  private audio: RenderedAudio | null = null;
  private source: AudioBufferSourceNode | null = null;
  private startedAt = 0;
  private offset = 0;
  private raf = 0;
  private scratch = new Float32Array(2048);

  private context(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctx = globalThis.AudioContext;
    if (!Ctx) return null;
    try {
      this.ctx = new Ctx({ sampleRate: this.audio?.rate ?? 48000, latencyHint: 'interactive' });
    } catch {
      this.ctx = new Ctx();
    }
    const ctx = this.ctx;
    this.master = ctx.createGain();
    this.master.gain.value = dbToGain(usePlayback.getState().monitorDb);
    this.splitter = ctx.createChannelSplitter(2);
    const l = ctx.createAnalyser();
    const r = ctx.createAnalyser();
    l.fftSize = r.fftSize = 2048;
    this.master.connect(ctx.destination);
    this.master.connect(this.splitter);
    this.splitter.connect(l, 0);
    this.splitter.connect(r, 1);
    this.meters = [l, r];
    if (this.audio) this.buffer = this.toBuffer(this.audio);
    return ctx;
  }

  private toBuffer(audio: RenderedAudio): AudioBuffer | null {
    const ctx = this.ctx;
    if (!ctx || audio.left.length === 0) return null;
    const buffer = ctx.createBuffer(2, audio.left.length, audio.rate);
    buffer.copyToChannel(audio.left as Float32Array<ArrayBuffer>, 0);
    buffer.copyToChannel((audio.right.length ? audio.right : audio.left) as Float32Array<ArrayBuffer>, 1);
    return buffer;
  }

  /** Current playhead in seconds. */
  position(): number {
    const state = usePlayback.getState();
    if (!this.source || !this.ctx) return state.position;
    let pos = this.offset + (this.ctx.currentTime - this.startedAt);
    const loop = state.loopOn ? state.loop : null;
    if (loop && loop.end - loop.start > 0.01 && pos >= loop.end) {
      const len = loop.end - loop.start;
      pos = loop.start + ((pos - loop.start) % len);
    }
    return Math.min(pos, state.duration);
  }

  /** Replace the rendered audio; playback continues from the same position. */
  setAudio(audio: RenderedAudio | null): void {
    const wasPlaying = usePlayback.getState().playing;
    const at = this.position();
    this.halt();
    this.audio = audio;
    this.buffer = audio && this.ctx ? this.toBuffer(audio) : null;
    const duration = audio ? audio.left.length / audio.rate : 0;
    usePlayback.setState({ duration, position: Math.min(at, duration) });
    if (wasPlaying && audio) this.play(Math.min(at, duration));
    else usePlayback.setState({ playing: false });
  }

  get hasAudio(): boolean {
    return !!this.audio && this.audio.left.length > 0;
  }

  play(from?: number): void {
    const ctx = this.context();
    if (!ctx || !this.audio) return;
    if (!this.buffer) this.buffer = this.toBuffer(this.audio);
    if (!this.buffer) return;
    void ctx.resume();
    this.halt();
    const state = usePlayback.getState();
    let start = Math.max(0, from ?? state.position);
    if (start >= state.duration - 0.001) start = 0;
    const loop = state.loopOn && state.loop && state.loop.end - state.loop.start > 0.01 ? state.loop : null;
    if (loop && (start < loop.start || start >= loop.end)) start = loop.start;
    const src = ctx.createBufferSource();
    src.buffer = this.buffer;
    if (loop) {
      src.loop = true;
      src.loopStart = loop.start;
      src.loopEnd = Math.min(loop.end, this.buffer.duration);
    }
    src.connect(this.master!);
    src.onended = () => {
      if (this.source !== src) return;
      this.source = null;
      usePlayback.setState({ playing: false, position: 0 });
    };
    src.start(0, start);
    this.source = src;
    this.startedAt = ctx.currentTime;
    this.offset = start;
    usePlayback.setState({ playing: true, position: start });
    this.tick();
  }

  /** Stop and keep the playhead where it is. */
  stop(): void {
    const at = this.position();
    this.halt();
    usePlayback.setState({ playing: false, position: at });
  }

  toggle(): void {
    if (usePlayback.getState().playing) this.stop();
    else this.play();
  }

  /** Move the playhead (keeps playing when playing). */
  seek(seconds: number): void {
    const state = usePlayback.getState();
    const t = Math.max(0, Math.min(state.duration || seconds, seconds));
    if (state.playing) this.play(t);
    else usePlayback.setState({ position: t });
  }

  setLoop(loop: LoopRegion | null): void {
    const clean = loop && loop.end > loop.start ? { start: Math.max(0, loop.start), end: loop.end } : null;
    usePlayback.setState({ loop: clean, loopOn: !!clean });
    if (usePlayback.getState().playing) this.play(this.position());
  }

  setLoopOn(on: boolean): void {
    usePlayback.setState({ loopOn: on });
    if (usePlayback.getState().playing) this.play(this.position());
  }

  setMonitor(db: number): void {
    usePlayback.setState({ monitorDb: db });
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(dbToGain(db), this.ctx.currentTime, 0.02);
  }

  /** Short audition tone for a MIDI pitch (piano roll / step preview). */
  previewNote(midi: number, velocity = 0.8, seconds = 0.35): void {
    const ctx = this.context();
    if (!ctx || !this.master) return;
    void ctx.resume();
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = 440 * Math.pow(2, (midi - 69) / 12);
    const peak = 0.25 * Math.max(0.05, Math.min(1, velocity));
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(peak, t + 0.005);
    env.gain.setTargetAtTime(peak * 0.6, t + 0.005, 0.08);
    env.gain.setTargetAtTime(0, t + seconds, 0.05);
    osc.connect(env).connect(this.master);
    osc.start(t);
    osc.stop(t + seconds + 0.4);
    this.tick();
  }

  /** Short noise click for drum step previews. */
  previewHit(velocity = 0.8): void {
    const ctx = this.context();
    if (!ctx || !this.master) return;
    void ctx.resume();
    const len = Math.floor(ctx.sampleRate * 0.08);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 4) * 0.4 * velocity;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.master);
    src.start();
    this.tick();
  }

  private halt(): void {
    const src = this.source;
    this.source = null;
    if (src) {
      src.onended = null;
      try {
        src.stop();
      } catch {
        // Already stopped.
      }
      src.disconnect();
    }
  }

  private peak(analyser: AnalyserNode): number {
    if (this.scratch.length !== analyser.fftSize) this.scratch = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(this.scratch);
    let p = 0;
    for (let i = 0; i < this.scratch.length; i++) p = Math.max(p, Math.abs(this.scratch[i]!));
    return p;
  }

  private tick(): void {
    if (this.raf) return;
    const step = () => {
      this.raf = 0;
      const state = usePlayback.getState();
      const update: Partial<PlaybackState> = {};
      if (this.source) update.position = this.position();
      if (this.meters) {
        const decay = (prev: number, now: number) => Math.max(now, Number.isFinite(prev) ? prev - 1.2 : MIN_DB);
        update.meterDb = decay(state.meterDb, gainToDb(this.peak(this.meters[0])));
        update.meterRightDb = decay(state.meterRightDb, gainToDb(this.peak(this.meters[1])));
        if (update.meterDb <= MIN_DB) update.meterDb = -Infinity;
        if (update.meterRightDb <= MIN_DB) update.meterRightDb = -Infinity;
      }
      usePlayback.setState(update);
      const active = !!this.source || Number.isFinite(update.meterDb ?? -Infinity) || Number.isFinite(update.meterRightDb ?? -Infinity);
      if (active && typeof requestAnimationFrame === 'function') this.raf = requestAnimationFrame(step);
    };
    if (typeof requestAnimationFrame === 'function') this.raf = requestAnimationFrame(step);
  }
}

export const soundPlayer = new SoundPlayer();
